import {
  AI_CHAT_MESSAGE_MAX_LENGTH,
  AIActions,
  selectAIGenerating,
} from '@store/reducers/ai'
import { selectAIRemainingRequests } from '@store/reducers/ai/selectors'
import { isArtBoardHalted } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { clsJoin } from '@/utils'
import { SubmitButton } from './components'
import css from './styles.module.css'

/** Show the "n / max" counter once the message is this close to the limit. */
const COUNTER_THRESHOLD = AI_CHAT_MESSAGE_MAX_LENGTH * 0.9

const formatCount = (count: number) => count.toLocaleString('en-US')

export const ChatInput = () => {
  const [value, setValue] = useState('')
  const dispatch = useDispatch()
  const generating = useSelector(selectAIGenerating)
  const remainingRequests = useSelector(selectAIRemainingRequests)
  const halted = useSelector(({ artboard }: AppState) =>
    isArtBoardHalted(artboard)
  )
  // Saving is down or the history is open: no new message. A turn already
  // running keeps its stop button — `disabled` takes the pointer off it.
  const disabled = remainingRequests === 0 || (halted && !generating)
  const active = generating || value.length > 0
  const nearLimit = value.length >= COUNTER_THRESHOLD
  const atLimit = value.length >= AI_CHAT_MESSAGE_MAX_LENGTH
  const counterText = `${formatCount(value.length)} / ${formatCount(AI_CHAT_MESSAGE_MAX_LENGTH)}`

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSubmitClick()
    }
  }

  const handleSubmitClick = () => {
    if (generating) {
      dispatch(AIActions.stopMessage())
    } else if (value.trim()) {
      dispatch(AIActions.sendMessage({ message: value }))
      setValue('')
    }
  }

  const handleOnFocus = () => {
    dispatch({
      type: CanvasActionTypes.CLEAR_ALL_SELECTION,
    })
  }

  const disableForm = generating || disabled

  const formCn = clsJoin(css.ai_form, disableForm && css.disabled)

  const textareaCn = clsJoin(css.ai_textarea, disableForm && css.disabled)

  return (
    <div className={formCn}>
      <textarea
        disabled={generating || disabled}
        className={textareaCn}
        placeholder="Ask Octopus..."
        maxLength={AI_CHAT_MESSAGE_MAX_LENGTH}
        value={value}
        onFocus={handleOnFocus}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
      />
      {nearLimit && (
        <span className={clsJoin(css.ai_counter, atLimit && css.at_limit)}>
          {counterText}
        </span>
      )}
      <SubmitButton
        generating={generating}
        active={active}
        disabled={disabled}
        handleSubmitClick={handleSubmitClick}
      />
    </div>
  )
}
